export const NOTE_KIND_RULES = {
  client_credit_ours: {
    title: "Credito nuestra al cliente",
    description: "Resta saldo a nuestro favor del contacto cliente (abono, como un cobro).",
    accountStatementSign: -1,
    clientCollectedSign: 1,
    supplierInvoiceSign: 0,
    textlistValues: {
      kind: "Credit note",
      direction: "Giving",
      contactRole: "Client",
    },
  },
  client_debit_ours: {
    title: "Debito nosotros al cliente",
    description: "Suma saldo a nuestro favor del contacto cliente (cargo, como Invoice Client).",
    accountStatementSign: 1,
    clientCollectedSign: -1,
    supplierInvoiceSign: 0,
    textlistValues: {
      kind: "Debit note",
      direction: "Giving",
      contactRole: "Client",
    },
  },
  supplier_credit_to_us: {
    title: "Credito de proveedor a nosotros",
    description: "Suma saldo a nuestro favor del proveedor (reduce lo que les debemos).",
    accountStatementSign: 1,
    clientCollectedSign: 0,
    supplierInvoiceSign: -1,
    textlistValues: {
      kind: "Credit note",
      direction: "Receiving",
      contactRole: "Supplier",
    },
  },
  supplier_debit_to_us: {
    title: "Debito de proveedor a nosotros",
    description: "Resta saldo a nuestro favor del proveedor (aumenta lo que les debemos).",
    accountStatementSign: -1,
    clientCollectedSign: 0,
    supplierInvoiceSign: 1,
    textlistValues: {
      kind: "Debit note",
      direction: "Receiving",
      contactRole: "Supplier",
    },
  },
  // Notas que emite el cliente contra nosotros: el efecto va al revés de la etiqueta
  // del documento (su ND nos reclama, su NC reconoce deuda a favor nuestro).
  client_debit_to_us: {
    title: "Debito del cliente a nosotros",
    description: "Resta saldo a nuestro favor del cliente (reclamo suyo, como una nota de credito nuestra).",
    accountStatementSign: -1,
    clientCollectedSign: 1,
    supplierInvoiceSign: 0,
    textlistValues: {
      kind: "Debit note",
      direction: "Receiving",
      contactRole: "Client",
    },
  },
  client_credit_to_us: {
    title: "Credito del cliente a nosotros",
    description: "Suma saldo a nuestro favor del cliente (reconoce que nos debe mas, como una nota de debito nuestra).",
    accountStatementSign: 1,
    clientCollectedSign: -1,
    supplierInvoiceSign: 0,
    textlistValues: {
      kind: "Credit note",
      direction: "Receiving",
      contactRole: "Client",
    },
  },
};

export function localCalendarIsoDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

let appliedToLineSeq = 0;

export function createEmptyAppliedToLine(partial = {}) {
  appliedToLineSeq += 1;
  return {
    key: partial.key || `at-${Date.now()}-${appliedToLineSeq}`,
    targetType: partial.targetType === "proform" ? "proform" : "shipping",
    shippingRef: partial.shippingRef || null,
    proformRef: partial.proformRef || null,
    amount: Number(partial.amount) || 0,
  };
}

export function createDefaultNotaComercialForm() {
  return {
    noteKind: "client_credit_ours",
    contactRef: null,
    date: localCalendarIsoDate(),
    currencyId: "",
    amount: 0,
    number: "",
    appliedToLines: [],
    description: "",
    comment: "",
    file: "",
    /** Rol del contacto cuando no es el genérico de la variante (el tipo del gasto). */
    contactRoleOverride: "",
    /**
     * Tope de lo que se puede aplicar al destino: lo que falta pagar de ese gasto.
     * null = sin tope conocido (el alta suelta, que no sale de una línea de gasto).
     */
    maxApplicable: null,
  };
}

/**
 * Moneda con la que abre una nota nueva: USD si la lista la tiene; si no, la primera.
 * @param {Array} lists listas de /api/gotrading/lists
 */
export function defaultNotaComercialCurrencyId(lists) {
  const list = (lists || []).find((l) => l && l.type === "currency");
  const rows = list && Array.isArray(list.values) ? list.values.filter((v) => v && v._id) : [];
  if (!rows.length) return "";
  const re = /\busd\b|u\.s\.?\s*d|\$\s*us|^\s*usd\s*$/i;
  const byLabel = rows.find((v) => re.test(String(v.label || "")));
  if (byLabel) return String(byLabel._id);
  const byCode = rows.find((v) => re.test(String(v.code || v.short || v.symbol || "")));
  if (byCode) return String(byCode._id);
  return String(rows[0]._id);
}

export function getReferenceId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value._id) return String(value._id);
  return "";
}

export function getNoteKindOptions(t) {
  const tr = typeof t === "function" ? t : (key) => key;
  return [
    { value: "client_credit_ours", label: tr("notaComercial.noteForm.kindClientCredit") },
    { value: "client_debit_ours", label: tr("notaComercial.noteForm.kindClientDebit") },
    { value: "supplier_credit_to_us", label: tr("notaComercial.noteForm.kindSupplierCredit") },
    { value: "supplier_debit_to_us", label: tr("notaComercial.noteForm.kindSupplierDebit") },
  ];
}

/** True when we issue the note (Giving) — only then we auto-number. */
export function noteKindUsesOurNumbering(noteKind) {
  const rule = NOTE_KIND_RULES[noteKind];
  return !!(rule && rule.textlistValues && rule.textlistValues.direction === "Giving");
}

/** «Client» o «Supplier»: qué contacto le corresponde a la variante. */
export function noteKindContactRole(noteKind) {
  const rule = NOTE_KIND_RULES[noteKind];
  return (rule && rule.textlistValues && rule.textlistValues.contactRole) || "Client";
}

/**
 * Contacto que corresponde a la variante elegida: el proveedor del embarque en las notas
 * del proveedor, el cliente en las nuestras. Devuelve "" si ese contacto no se conoce.
 * @param {string} noteKind
 * @param {{ clientId?: string, supplierId?: string }} contacts
 */
export function contactIdForNoteKind(noteKind, contacts = {}) {
  const wanted =
    noteKindContactRole(noteKind) === "Supplier" ? contacts.supplierId : contacts.clientId;
  return wanted ? String(wanted) : "";
}

/**
 * Deja el contacto del formulario alineado con la variante. Si el contacto de esa
 * variante no se conoce lo limpia, para no dejar puesto el de la otra parte.
 * @param {object} form
 * @param {{ clientId?: string, supplierId?: string }} contacts
 */
export function applyNoteKindContact(form, contacts = {}) {
  if (!form) return form;
  const nextContact = contactIdForNoteKind(form.noteKind, contacts);
  const currentId = getReferenceId(form.contactRef);
  if (nextContact) {
    if (currentId === nextContact) return form;
    return { ...form, contactRef: nextContact };
  }
  const other =
    noteKindContactRole(form.noteKind) === "Supplier" ? contacts.clientId : contacts.supplierId;
  // El contacto puesto era el de la otra parte: sacarlo y que se elija a mano.
  if (other && currentId === String(other)) {
    return { ...form, contactRef: null };
  }
  return form;
}

export function noteKindIsCredit(noteKind) {
  const rule = NOTE_KIND_RULES[noteKind];
  return !!(rule && rule.textlistValues && rule.textlistValues.kind === "Credit note");
}

export function getRule(noteKind) {
  return NOTE_KIND_RULES[noteKind] || NOTE_KIND_RULES.client_credit_ours;
}

/**
 * Variante (clave de NOTE_KIND_RULES) de una nota ya guardada, leida de los valores
 * canonicos del documento. Sirve para describir la nota recien creada sin depender
 * del estado del formulario, que puede haber cambiado despues del alta.
 */
export function noteKindFromDoc(doc) {
  if (!doc) return "";
  const kind = String(doc.credit_debit || "").trim();
  const direction = String(doc.flow_direction || "").trim();
  const contactRole = String(doc.contact_role || "").trim();
  return (
    Object.keys(NOTE_KIND_RULES).find((key) => {
      const values = NOTE_KIND_RULES[key].textlistValues;
      return values.kind === kind && values.direction === direction && values.contactRole === contactRole;
    }) || ""
  );
}

export function sumAppliedToLines(lines) {
  if (!Array.isArray(lines) || !lines.length) return 0;
  return lines.reduce((sum, line) => sum + (Number(line && line.amount) || 0), 0);
}

export function remainingAppliedToAmount(form) {
  const total = Number(form && form.amount) || 0;
  return Math.max(0, total - sumAppliedToLines(form && form.appliedToLines));
}

export function appliedToExceedsAmount(form) {
  const total = Number(form && form.amount) || 0;
  return sumAppliedToLines(form && form.appliedToLines) > total + 1e-6;
}

/**
 * Tope de lo aplicable al destino, o null si no se conoce.
 * Es lo que falta pagar del gasto: aplicar de más lo deja en negativo.
 */
export function appliedToTargetCap(form) {
  const raw = form && form.maxApplicable;
  // Ojo: Number(null) es 0. Sin tope cargado no hay tope, no un tope de cero.
  if (raw === null || raw === undefined || raw === "") return null;
  const cap = Number(raw);
  return Number.isFinite(cap) && cap >= 0 ? cap : null;
}

/**
 * Tope de una línea: amount principal − suma del resto de líneas (≥ 0), y nunca más de
 * lo que falta pagar del destino. El excedente no se pierde: queda sin aplicar en la
 * nota, para usarlo en otra operación.
 */
export function maxAmountForAppliedToLine(form, lineIndex) {
  const total = Number(form && form.amount) || 0;
  const lines = Array.isArray(form && form.appliedToLines) ? form.appliedToLines : [];
  const others = lines.reduce((sum, line, i) => {
    if (i === lineIndex) return sum;
    return sum + (Number(line && line.amount) || 0);
  }, 0);
  const byNoteAmount = Math.max(0, total - others);
  const cap = appliedToTargetCap(form);
  return cap === null ? byNoteAmount : Math.min(byNoteAmount, cap);
}

export function clampAppliedToLineAmount(form, lineIndex, rawAmount) {
  const max = maxAmountForAppliedToLine(form, lineIndex);
  const num = Number(rawAmount);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.min(num, max);
}

/**
 * Si la suma de líneas supera el amount principal, recorta de punta a punta
 * para que no se pueda aplicar más del total.
 */
export function clampAllAppliedToLinesToAmount(form) {
  if (!form || !Array.isArray(form.appliedToLines) || !form.appliedToLines.length) {
    return form;
  }
  if (form.appliedToLines.length === 1) {
    return syncSingleAppliedToLineAmount(form);
  }
  if (!appliedToExceedsAmount(form)) return form;

  let remainingBudget = Number(form.amount) || 0;
  const nextLines = form.appliedToLines.map((line) => {
    const current = Number(line && line.amount) || 0;
    const capped = Math.min(current, Math.max(0, remainingBudget));
    remainingBudget = Math.max(0, remainingBudget - capped);
    if (capped === current) return line;
    return { ...line, amount: capped };
  });

  return {
    ...form,
    appliedToLines: nextLines,
  };
}

/**
 * Si hay una sola línea applied_to, alinea su monto con el total de la nota
 * (mismo comportamiento que el alta rápida de un solo destino).
 */
export function syncSingleAppliedToLineAmount(form) {
  if (!form || !Array.isArray(form.appliedToLines) || form.appliedToLines.length !== 1) {
    return form;
  }
  const total = Number(form.amount) || 0;
  const cap = appliedToTargetCap(form);
  // Se aplica lo que falta pagar y nada más: el resto queda sin aplicar en la nota.
  const amount = cap === null ? total : Math.min(total, cap);
  const [only] = form.appliedToLines;
  if (Number(only.amount) === amount) return form;
  return {
    ...form,
    appliedToLines: [{ ...only, amount }],
  };
}

/**
 * ¿La nota no se puede aplicar porque el gasto de destino ya está pago entero?
 * Pasa al abrirla desde una línea de gasto sin saldo: el tope es cero.
 */
function appliedToNothingPending(form) {
  if (appliedToTargetCap(form) !== 0) return false;
  const lines = Array.isArray(form && form.appliedToLines) ? form.appliedToLines : [];
  return lines.some((line) => !((Number(line && line.amount) || 0) > 0));
}

export function validateNotaComercialForm(form, t) {
  const tr = typeof t === "function" ? t : (key) => key;
  if (!getReferenceId(form.contactRef)) return tr("notaComercial.noteForm.validationContact");
  if (!(Number(form.amount) > 0)) return tr("notaComercial.noteForm.validationAmount");

  const lines = Array.isArray(form.appliedToLines) ? form.appliedToLines : [];
  for (const line of lines) {
    const lineAmount = Number(line && line.amount) || 0;
    if (!(lineAmount > 0)) {
      // Tope cero = el gasto ya está pago: el mensaje genérico no le dice nada al usuario.
      if (appliedToNothingPending(form)) {
        return tr("notaComercial.noteForm.validationAppliedNothingPending");
      }
      return tr("notaComercial.noteForm.validationAppliedAmount");
    }
    if (line.targetType === "shipping" && !getReferenceId(line.shippingRef)) {
      return tr("notaComercial.noteForm.validationShipping");
    }
    if (line.targetType === "proform" && !getReferenceId(line.proformRef)) {
      return tr("notaComercial.noteForm.validationProform");
    }
  }

  const appliedSum = sumAppliedToLines(lines);
  const total = Number(form.amount) || 0;
  if (appliedSum > total + 1e-6) {
    return tr("notaComercial.noteForm.validationAppliedExceeds");
  }

  return "";
}

/**
 * ¿La nota la recibimos del contacto? Esas son documento ajeno: conviene adjuntarlo.
 * @param {string} noteKind
 * @returns {boolean}
 */
export function noteKindIsReceived(noteKind) {
  return getRule(noteKind).textlistValues.direction === "Receiving";
}

/**
 * ¿Falta el adjunto en una nota que recibimos?
 *
 * Las que emitimos nosotros generan su propio PDF, así que ahí no hay nada que adjuntar;
 * las que nos dan el proveedor o el cliente llegan como documento y sin el archivo la
 * nota queda sin respaldo. No bloquea: solo hay que avisar antes de guardar.
 *
 * @param {object} form
 * @returns {boolean}
 */
export function noteMissingReceivedFile(form) {
  if (!form || !noteKindIsReceived(form.noteKind)) return false;
  const raw = form.file;
  if (Array.isArray(raw)) return raw.filter(Boolean).length === 0;
  return !String(raw || "").trim();
}

export function buildAppliedToPayloadLines(form) {
  const lines = Array.isArray(form.appliedToLines) ? form.appliedToLines : [];
  const payloadLines = [];

  for (const line of lines) {
    const amount = Number(line && line.amount) || 0;
    if (!(amount > 0)) continue;

    if (line.targetType === "proform") {
      const proformId = getReferenceId(line.proformRef);
      if (!proformId) continue;
      payloadLines.push({ proform: proformId, amount });
      continue;
    }

    const shippingId = getReferenceId(line.shippingRef);
    if (!shippingId) continue;
    payloadLines.push({ shipping: shippingId, amount });
  }

  return payloadLines;
}

/**
 * Adjuntos de la nota como array. El campo pasó a múltiple, pero un formulario viejo
 * o un valor ya guardado pueden traer todavía un string suelto.
 *
 * @param {string[]|string|null} value
 * @returns {string[]}
 */
function normalizeFileList(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  const single = typeof value === "string" ? value.trim() : "";
  return single ? [single] : [];
}

/**
 * Rol del contacto con el que se va a guardar la nota.
 *
 * Por defecto el de la variante (Client o Supplier). Si la nota nació de una línea de
 * gasto, el rol es el tipo de esa línea (Freight, Courrier…), pero solo en las variantes
 * de proveedor: al pasar a una de cliente vuelve a Client y la nota sigue clasificando.
 *
 * @param {object} form
 * @returns {string}
 */
export function contactRoleForForm(form) {
  const rule = getRule(form && form.noteKind);
  const base = rule.textlistValues.contactRole;
  const override = String((form && form.contactRoleOverride) || "").trim();
  return base === "Supplier" && override ? override : base;
}

export function buildNotaComercialCreatePayload(form, options = {}) {
  const rule = getRule(form.noteKind);
  const { kind, direction } = rule.textlistValues;
  const nowIso = new Date().toISOString();
  const dateValue = form.date || nowIso.slice(0, 10);
  const amount = Number(form.amount) || 0;
  const numberRaw = form.number != null ? String(form.number).trim() : "";
  let applied_to = buildAppliedToPayloadLines(form);

  // Desde Finance de shipping/proforma con asignación bloqueada: garantizar applied_to
  // aunque la línea haya quedado con monto 0 antes del sync.
  if ((!applied_to || !applied_to.length) && amount > 0) {
    const lines = Array.isArray(form.appliedToLines) ? form.appliedToLines : [];
    const first = lines[0];
    if (first && first.targetType === "shipping") {
      const shippingId = getReferenceId(first.shippingRef);
      if (shippingId) applied_to = [{ shipping: shippingId, amount }];
    } else if (first && first.targetType === "proform") {
      const proformId = getReferenceId(first.proformRef);
      if (proformId) applied_to = [{ proform: proformId, amount }];
    }
  }

  const payload = {
    date: dateValue,
    contact: getReferenceId(form.contactRef),
    currency: form.currencyId || null,
    credit_debit: kind,
    flow_direction: direction,
    contact_role: contactRoleForForm(form),
    amount,
    description: form.description || "",
    comment: form.comment || "",
    applied_to,
  };
  // El adjunto es múltiple. Un array vacío sí se manda: es como se borran los adjuntos.
  if (form.file !== undefined && form.file !== null) {
    payload.file = normalizeFileList(form.file);
  }
  if (numberRaw) {
    payload.number = numberRaw;
    if (direction === "Giving" && kind === "Credit note") {
      payload.number_credit = numberRaw;
    }
    if (direction === "Giving" && kind === "Debit note") {
      payload.number_debit = numberRaw;
    }
  }
  if (form.number_date) {
    payload.number_date = form.number_date;
  }
  void options;
  return payload;
}

/**
 * Estado inicial del formulario a partir del contexto de la pantalla que lo abre.
 *
 * @param {object} context
 * @param {string} [context.sourceModel] modelo de la pantalla («shipping» / «proform»)
 * @param {object} [context.itemData] item de la pantalla
 * @param {string} [context.clientId] contacto cliente de la operación
 * @param {string} [context.supplierId] contacto proveedor del embarque
 * @param {string} [context.noteKind] variante inicial
 * @param {{type: 'shipping'|'proform', id: string, name?: string}} [context.appliedTo]
 *   destino explícito de la aplicación, para cuando no coincide con el item de la pantalla
 *   (ej.: la nota de un embarque abierta desde la pantalla de la proforma)
 * @param {string} [context.currencyId]
 */
export function buildNotaComercialFormPrefill(context = {}) {
  const form = createDefaultNotaComercialForm();
  const modelName = String(context.sourceModel || context.modelName || "").toLowerCase();
  const itemData = context.itemData || null;
  const clientId = context.clientId ? String(context.clientId) : "";
  const supplierId = context.supplierId ? String(context.supplierId) : "";

  if (context.noteKind && NOTE_KIND_RULES[context.noteKind]) {
    form.noteKind = context.noteKind;
  }

  const byKind = contactIdForNoteKind(form.noteKind, { clientId, supplierId });
  if (byKind) {
    form.contactRef = byKind;
  } else if (context.contactId) {
    form.contactRef = String(context.contactId);
  } else if (itemData && itemData.client) {
    form.contactRef = itemData.client;
  }

  // Destino explícito: gana sobre el item de la pantalla.
  const explicit = context.appliedTo;
  if (explicit && explicit.id) {
    const isProform = explicit.type === "proform";
    form.appliedToLines = [
      createEmptyAppliedToLine({
        targetType: isProform ? "proform" : "shipping",
        proformRef: isProform ? { _id: String(explicit.id), name: explicit.name || "" } : null,
        shippingRef: isProform ? null : { _id: String(explicit.id), name: explicit.name || "" },
        amount: 0,
      }),
    ];
  } else if (modelName === "proform" && itemData) {
    form.appliedToLines = [
      createEmptyAppliedToLine({
        targetType: "proform",
        proformRef: itemData,
        amount: 0,
      }),
    ];
  } else if (modelName === "shipping" && itemData) {
    form.appliedToLines = [
      createEmptyAppliedToLine({
        targetType: "shipping",
        shippingRef: itemData,
        amount: 0,
      }),
    ];
  }

  if (context.currencyId) {
    form.currencyId = String(context.currencyId);
  }
  const cap = Number(context.maxApplicable);
  if (Number.isFinite(cap) && cap >= 0) {
    form.maxApplicable = cap;
  }
  if (context.contactRoleOverride) {
    form.contactRoleOverride = String(context.contactRoleOverride);
  }
  if (Number(context.amount) > 0) {
    form.amount = Number(context.amount);
  }

  return form;
}
